import { MenuItem, SvgIcon } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../enums';
import { IMenuItem } from '../types';
import { useIsContentOverflowing } from '../utils/hooks/useIsContentOverflowing';
import { handleSubmenuItemKeyDown } from '../utils/keyboardNavUtils';

import { FavoriteStarButton } from './FavoriteStarButton';
import { SubmenuItemText } from './SubmenuItemText';
import { SubmenuItemTooltip } from './SubmenuItemTooltip';

const styles = {
  container: (isSelected: boolean, hasRightArrowIcon: boolean) => {
    return {
      alignItems: 'center',
      border: 'none',
      color: isSelected && hasRightArrowIcon ? 'primary.main' : 'text.primary',
      cursor: isSelected || !hasRightArrowIcon ? 'default' : 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      padding: 0.5,
      paddingLeft: 1.5,
      paddingRight: hasRightArrowIcon ? 2 : 1,
      width: '100%'
    };
  }
};

interface ISubmenuItemProps {
  item: Pick<IMenuItem, 'label' | 'id' | 'url' | 'openInNewTab' | 'onClick' | 'linkComponent'>;
  isSelected: boolean;
  isFavorited: boolean;
  icon?: 'favorite' | 'rightArrow';
  lightFontWeight?: boolean;
  autoFocus?: boolean;
  handleKeyDown(event: React.KeyboardEvent): void;
  handleMenuClick(): void;
  handleFavoriteIconClick(isItemFavorited: boolean): Promise<void>;
}

export const SubmenuItem = React.forwardRef<HTMLLIElement, ISubmenuItemProps>(
  (
    {
      item,
      isSelected,
      isFavorited,
      icon,
      lightFontWeight,
      autoFocus,
      handleKeyDown,
      handleMenuClick,
      handleFavoriteIconClick
    },
    ref
  ) => {
    const starRef = React.useRef(null);
    const labelRef = React.useRef(null);
    const showTooltip = useIsContentOverflowing(labelRef, item.label);

    const innerItemsAreFocusable = () => !!(starRef.current && labelRef.current);
    // Use outer tooltip if inner items are not focusable,
    // that way we can have tooltip show up when focused on outermost item
    const useOuterTooltip = () => showTooltip && !innerItemsAreFocusable();
    const useInnerTooltip = () => showTooltip && innerItemsAreFocusable();
    return (
      <SubmenuItemTooltip title={useOuterTooltip() ? item.label : ''}>
        <MenuItem
          ref={ref}
          sx={styles.container(isSelected, icon === 'rightArrow')}
          data-test={getDynamicDataTestId(DATA_TEST.submenu_item, `${item.id}_${item.url ? '_link' : ''}`)}
          data-og={getDynamicDataOGId(DATA_OG.menu_item, item.label)}
          autoFocus={autoFocus}
          onKeyDown={(e) => {
            handleKeyDown(e);
            handleSubmenuItemKeyDown(e, innerItemsAreFocusable, item, labelRef);
          }}
          onClick={() => {
            if (!item.url && !item.onClick) handleMenuClick();
            // Otherwise the click should be handled by the inner Link/button component
          }}
        >
          <SubmenuItemText
            showTooltip={useInnerTooltip()}
            item={item}
            lightFontWeight={Boolean(lightFontWeight)}
            labelRef={labelRef}
            starRef={starRef}
            handleMenuClick={handleMenuClick}
          />
          {icon === 'rightArrow' && (
            <SvgIcon data-test={DATA_TEST.right_arrow_icon} inheritViewBox component={ChevronRightIcon} />
          )}

          {icon === 'favorite' && (
            <FavoriteStarButton
              labelRef={labelRef}
              starRef={starRef}
              favorited={isFavorited}
              onClick={async (e) => {
                e.stopPropagation();
                await handleFavoriteIconClick(isFavorited);
              }}
              styles={{ marginLeft: 0.5 }}
            />
          )}
        </MenuItem>
      </SubmenuItemTooltip>
    );
  }
);
